import React from 'react';
import { MenuItem, Tooltip, TooltipProps } from '@chakra-ui/react';
import NotificationsOffSharpIcon from '@gamma/ui/icons/NotificationsOffSharpIcon';
import NotificationsSharpIcon from '@gamma/ui/icons/NotificationsSharpIcon';
import NotificationsOffOutlinedIcon from '@gamma/ui/icons/NotificationsOffOutlinedIcon';
import NotificationsOutlinedIcon from '@gamma/ui/icons/NotificationsOutlinedIcon';
import { LoadingSpinner } from '@gamma/ui/progress';
import { i18n } from '@investigator/shared/localization';

export interface SuppressEntityMenuItemProps {
  isSuppressed?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  showSuppressEntity?: boolean;
  onClick: () => void;
  testId?: string;
  tooltipPlacement?: TooltipProps['placement'];
  tooltipOffset?: TooltipProps['offset'];
  shouldWrapChildren?: boolean;
  customTooltipLabel?: string;
  useOutlinedIcons?: boolean;
  suppressText?: string;
  unsuppressText?: string;
}

const { entitySuppressTooltip } = i18n.pages.entities;
const { suppress, unSuppress } = i18n.pages.entityDetails.excludeEntity;

export const SuppressEntityMenuItem: React.FC<SuppressEntityMenuItemProps> = ({
  isSuppressed = false,
  isDisabled = false,
  isLoading = false,
  showSuppressEntity = true,
  onClick,
  testId = 'suppress-entity-trigger',
  tooltipPlacement = 'left-start',
  tooltipOffset = [-9, 6],
  shouldWrapChildren = true,
  customTooltipLabel,
  useOutlinedIcons = false,
  suppressText = suppress,
  unsuppressText = unSuppress,
}) => {
  // Determine tooltip label
  const getTooltipLabel = () => {
    if (customTooltipLabel) return customTooltipLabel;
    if (!showSuppressEntity) return entitySuppressTooltip;
    if (isDisabled) return entitySuppressTooltip;
    return '';
  };

  // Determine icon based on state and icon variant
  const getIcon = () => {
    if (isLoading) {
      return <LoadingSpinner size="xs" minH="auto" />;
    }

    if (useOutlinedIcons) {
      return isSuppressed ? (
        <NotificationsOutlinedIcon boxSize={5} />
      ) : (
        <NotificationsOffOutlinedIcon boxSize={5} />
      );
    }

    return isSuppressed ? (
      <NotificationsSharpIcon boxSize={5} />
    ) : (
      <NotificationsOffSharpIcon boxSize={5} />
    );
  };

  // Determine menu text
  const getMenuText = () => {
    return isSuppressed ? unsuppressText : suppressText;
  };

  return (
    <Tooltip
      offset={tooltipOffset}
      placement={tooltipPlacement}
      shouldWrapChildren={shouldWrapChildren}
      label={getTooltipLabel()}
    >
      <MenuItem
        isDisabled={isDisabled || !showSuppressEntity || isLoading}
        data-testid={testId}
        onClick={onClick}
        icon={getIcon()}
      >
        {getMenuText()}
      </MenuItem>
    </Tooltip>
  );
};
